import { DateTime } from 'luxon';
import moment, { Moment } from 'moment';

/**
 * Converts a date and optional time to a Unix timestamp in milliseconds for Europe/Berlin timezone.
 *
 * @param date - The date value from the datepicker (can be Date object, Moment object, string, or number timestamp)
 * @param time - Optional time string in HH:mm format (24-hour). If not provided, defaults to 00:00:33
 * @returns Unix timestamp in milliseconds, or 0 if date is null/undefined
 *
 * @example
 * convertDateTimeToTimestamp(new Date('2025-01-15'), '14:30')
 *
 * @example
 * convertDateTimeToTimestamp(new Date('2025-01-15'), null)
 *
 * @example
 * convertDateTimeToTimestamp(1705329033000, null)
 *
 * @example
 * convertDateTimeToTimestamp(moment('2025-01-15'), '14:30')
 */
export function convertDateTimeToTimestamp(
    date: Date | Moment | string | number | null | undefined,
    time: string | null | undefined
): number {
    // Return 0 if no date is provided
    if (!date) {
        return 0;
    }

    // Convert date to a Date object based on its type
    let dateObj: Date;
    if (typeof date === 'string') {
        dateObj = new Date(date);
    } else if (typeof date === 'number') {
        dateObj = new Date(date);
    } else if (moment.isMoment(date)) {
        dateObj = date.toDate();
    } else {
        dateObj = date;
    }

    // Extract year, month, and day from the date
    const year = dateObj.getFullYear();
    const month = dateObj.getMonth() + 1; // Luxon uses 1-12 for months
    const day = dateObj.getDate();

    // Parse time if provided, otherwise default to 00:00:33
    let hours = 0;
    let minutes = 0;
    let seconds = 33;

    if (time && time.trim() !== '') {
        const timeParts = time.split(':');
        if (timeParts.length === 2) {
            hours = parseInt(timeParts[0], 10);
            minutes = parseInt(timeParts[1], 10);
            seconds = 0; // When time is explicitly provided, use 00 seconds
        }
    }

    // Create a DateTime object in Europe/Berlin timezone
    const dateTime = DateTime.fromObject(
        {
            year,
            month,
            day,
            hour: hours,
            minute: minutes,
            second: seconds,
        },
        { zone: 'Europe/Berlin' }
    );

    // Return Unix timestamp in milliseconds
    return dateTime.toMillis();
}

/**
 * Converts a Unix timestamp in milliseconds to separate date and time components.
 *
 * @param timestamp - Unix timestamp in milliseconds
 * @returns Object with date (Date object) and time (HH:mm string), or null values if timestamp is 0 or falsy
 *
 * @example
 * const result = convertTimestampToDateTime(1705329033000);
 * // result.date = Date object for 2025-01-15
 * // result.time = "14:30" (if not 00:00:33) or null (if 00:00:33)
 */
export function convertTimestampToDateTime(
    timestamp: number | null | undefined
): {
    date: Date | null;
    time: string | null;
} {
    // Return null values if no timestamp is provided
    if (!timestamp || timestamp === 0) {
        return { date: null, time: null };
    }

    // Convert timestamp to DateTime in Europe/Berlin timezone
    const dateTime = DateTime.fromMillis(timestamp, { zone: 'Europe/Berlin' });

    // Create a Date object for the date picker
    const date = new Date(
        dateTime.year,
        dateTime.month - 1, // JavaScript Date uses 0-11 for months
        dateTime.day
    );

    // Check if the time is the default 00:00:33
    // If so, return null for time (to keep the field empty)
    // Otherwise, return the time in HH:mm format
    let time: string | null = null;
    if (
        dateTime.hour !== 0 ||
        dateTime.minute !== 0 ||
        dateTime.second !== 33
    ) {
        // Format time as HH:mm
        const hourStr = dateTime.hour.toString().padStart(2, '0');
        const minuteStr = dateTime.minute.toString().padStart(2, '0');
        time = `${hourStr}:${minuteStr}`;
    }

    return { date, time };
}

/**
 * Determines the color class for a timestamp based on its proximity to the scheduled time.
 * Used for color-coding delivery note times.
 *
 * @param timestamp - Unix timestamp in milliseconds (scheduled time)
 * @param showTime - Whether the time should be displayed (from API showDateAnnouncedTime flag)
 * @param status - Delivery note or bundle status (e.g., 'open', 'sent', 'closed')
 * @returns Tailwind CSS color class or empty string
 *
 * Color scheme:
 * - More than 1 hour before: Orange (approaching)
 * - Within 1 hour before to exact time: Green (on time)
 * - Past scheduled time: Red (late)
 * - Closed or no timestamp or showTime=false: No color
 *
 * @example
 * // Scheduled for 2 hours from now with time display enabled
 * getTimeStatusColor(futureTimestamp, true, 'open') // returns 'tw-text-orange-600'
 *
 * @example
 * // Scheduled for 30 minutes from now with time display enabled
 * getTimeStatusColor(soonTimestamp, true, 'open') // returns 'tw-text-green-600'
 *
 * @example
 * // Scheduled time has passed with time display enabled
 * getTimeStatusColor(pastTimestamp, true, 'open') // returns 'tw-text-red-600'
 *
 * @example
 * // Delivery note is closed
 * getTimeStatusColor(anyTimestamp, true, 'closed') // returns ''
 *
 * @example
 * // Time display is disabled
 * getTimeStatusColor(anyTimestamp, false, 'open') // returns ''
 */
export function getTimeStatusColor(
    timestamp: number | null | undefined,
    showTime: boolean | null | undefined,
    status?: string | null
): string {
    // No coloring if no timestamp, status is closed, or time display is disabled
    if (!timestamp || timestamp === 0 || status === 'closed' || !showTime) {
        return '';
    }

    const now = DateTime.now().setZone('Europe/Berlin');
    const scheduled = DateTime.fromMillis(timestamp, {
        zone: 'Europe/Berlin',
    });
    const diffInHours = scheduled.diff(now, 'hours').hours;

    // More than 1 hour before scheduled time - Orange (approaching)
    if (diffInHours > 1) {
        return 'tw-text-orange-600';
    }
    // Within 1 hour before to exact time - Green (on time)
    else if (diffInHours >= 0) {
        return 'tw-text-green-600';
    }
    // Past scheduled time - Red (late)
    else {
        return 'tw-text-red-600';
    }
}
